import { Switch, Route, Redirect, useLocation } from "wouter";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { queryClient, apiRequest } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppearanceProvider } from "@/lib/appearance";
import AppSidebar from "@/components/AppSidebar";
import ThemeToggle from "@/components/ThemeToggle";
import BetaDisclaimerModal from "@/components/BetaDisclaimerModal";
import DisplayNamePrompt from "@/components/DisplayNamePrompt";
import { useAuth } from "@/hooks/useAuth";
import { getDeviceTimezone } from "@/lib/timezone";
import { rebindPushSubscription } from "@/lib/pushSubscribe";
import { startRealtime, stopRealtime } from "@/lib/realtime";
import { lockVault, setIdleLockMinutes } from "@/lib/vault/vaultState";
import { useQuery } from "@tanstack/react-query";
// Task #348 — Landing stays an eager import so logged-out first paint is
// instant; every other page is lazy-loaded so visitors (and Google's
// performance analysis) only download the landing chunk up front. Route
// structure is unchanged; only the imports are deferred.
import Landing from "@/pages/Landing";
const Home = lazy(() => import("@/pages/Home"));
const StepsPage = lazy(() => import("@/pages/StepsPage"));
const StepView = lazy(() => import("@/pages/StepView"));
const SponseesDashboard = lazy(() => import("@/pages/SponseesDashboard"));
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const RoleSelection = lazy(() => import("@/pages/RoleSelection"));
const Settings = lazy(() => import("@/pages/Settings"));
const ExpandedFamilyTree = lazy(() => import("@/pages/ExpandedFamilyTree"));
const Profile = lazy(() => import("@/pages/Profile"));
const SponsorLibrary = lazy(() => import("@/pages/SponsorLibrary"));
const AssignedTasks = lazy(() => import("@/pages/AssignedTasks"));
const AssignmentDetail = lazy(() => import("@/pages/AssignmentDetail"));
const AssignTasksToSponsee = lazy(() => import("@/pages/AssignTasksToSponsee"));
const StandardTasks = lazy(() => import("@/pages/StandardTasks"));
const SponseeAssignments = lazy(() => import("@/pages/SponseeAssignments"));
const SponseeSubmissions = lazy(() => import("@/pages/SponseeSubmissions"));
const SponseeMessages = lazy(() => import("@/pages/SponseeMessages"));
const SponseeChat = lazy(() => import("@/pages/SponseeChat"));
const Journal = lazy(() => import("@/pages/Journal"));
const SharedJournal = lazy(() => import("@/pages/SharedJournal"));
const TriggersCravings = lazy(() => import("@/pages/TriggersCravings"));
const SummaryArchive = lazy(() => import("@/pages/SummaryArchive"));
const SavedReadings = lazy(() => import("@/pages/SavedReadings"));
const Anniversaries = lazy(() => import("@/pages/Anniversaries"));
const ServicePage = lazy(() => import("@/pages/Service"));
const SponsorPortal = lazy(() => import("@/pages/SponsorPortal"));
const SponsorSettings = lazy(() => import("@/pages/SponsorSettings"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Resources = lazy(() => import("@/pages/Resources"));
const HomeGroupPortal = lazy(() => import("@/pages/HomeGroupPortal"));
const GratitudeLists = lazy(() => import("@/pages/GratitudeLists"));
const FindASponsorPage = lazy(() => import("@/pages/FindASponsorPage"));
const InnerCircle = lazy(() => import("@/pages/InnerCircle"));
const SponsorContactInbox = lazy(() => import("@/pages/SponsorContactInbox"));
import BurningDesireButton from "@/components/BurningDesireButton";
import BottomNav from "@/components/BottomNav";
import NotificationCenter from "@/components/NotificationCenter";
import EnablePushPrompt from "@/components/EnablePushPrompt";
import IosPwaTextSizeHint from "@/components/IosPwaTextSizeHint";
const RecoveryPlaybook = lazy(() => import("@/pages/RecoveryPlaybook"));
const First90Intro = lazy(() => import("@/pages/First90Intro"));
const Journey = lazy(() => import("@/pages/Journey"));
const DeviceStats = lazy(() => import("@/pages/DeviceStats"));
const AdminPortal = lazy(() => import("@/pages/AdminPortal"));
const WhatsNew = lazy(() => import("@/pages/WhatsNew"));
const Contact = lazy(() => import("@/pages/Contact"));
const NotFound = lazy(() => import("@/pages/not-found"));
import VaultGate from "@/components/vault/VaultGate";
import VaultStatusBadge from "@/components/vault/VaultStatusBadge";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useSponsorHomeDefault } from "@/hooks/useSponsorHomeDefault";

/**
 * Task #282 — one-time launch redirect for sponsors who opted into
 * "Show Home as Sponsor Dashboard". Fires at most once per app load, and
 * only if the user is still sitting on "/" when the preference loads —
 * any navigation the user makes first cancels it. All later "/" visits
 * (side-menu "Dashboard", direct links) are untouched.
 */
export function SponsorHomeLaunchRedirect() {
  const [location, setLocation] = useLocation();
  const { homeHref, isLoaded } = useSponsorHomeDefault();
  const doneRef = useRef(false);
  const navigatedAwayRef = useRef(false);

  useEffect(() => {
    if (location !== "/") navigatedAwayRef.current = true;
  }, [location]);

  useEffect(() => {
    if (doneRef.current || !isLoaded) return;
    doneRef.current = true;
    if (homeHref === "/sponsor-portal" && location === "/" && !navigatedAwayRef.current) {
      setLocation("/sponsor-portal", { replace: true });
    }
  }, [isLoaded, homeHref, location, setLocation]);

  return null;
}

function Router() {
  const { isAuthenticated, isLoading, user } = useAuth();
  // Task #53 — feeds the page-level ErrorBoundary's resetKey so a caught
  // error clears automatically when the user navigates to another route.
  const [currentLocation] = useLocation();
  const hasAutoDetectedTimezone = useRef(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Auto-detect and save timezone when user logs in without one set
  useEffect(() => {
    if (isAuthenticated && user && !hasAutoDetectedTimezone.current) {
      // Only auto-detect if user has default UTC or no timezone
      if (!user.timezone || user.timezone === "UTC") {
        const deviceTimezone = getDeviceTimezone();
        // Only update if device timezone differs from current
        if (deviceTimezone !== "UTC" && deviceTimezone !== user.timezone) {
          hasAutoDetectedTimezone.current = true;
          apiRequest("PATCH", "/api/user/timezone", { timezone: deviceTimezone })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
            })
            .catch(() => {
              // Silent fail - user can manually set timezone later
            });
        }
      }
    }
  }, [isAuthenticated, user]);

  // Task #325 — silently re-bind this device's push subscription to the new
  // login session (pushes are session-gated server-side; logout silences
  // them, re-login re-enables them without any user action). Also tell the
  // service worker auth is live again so its push handler may badge.
  const lastReboundUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    // Tell the SW auth is alive on EVERY authenticated render pass (not just
    // once per user id) so a dead-marker set by a transient 401 self-heals.
    // Send via both controller and the registration's active worker —
    // controller can be null right after a hard reload.
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: "auth-ok" });
      navigator.serviceWorker?.ready
        ?.then((reg) => reg.active?.postMessage({ type: "auth-ok" }))
        .catch(() => {});
    } catch {
      /* best-effort */
    }
    if (lastReboundUserIdRef.current === user.id) return;
    lastReboundUserIdRef.current = user.id;
    rebindPushSubscription().catch(() => {
      // Silent — push re-binding is best-effort.
    });
  }, [isAuthenticated, user?.id]);

  // Sync disclaimer state from user data
  useEffect(() => {
    if (user?.hasAcceptedDisclaimer) {
      setDisclaimerAccepted(true);
    }
  }, [user?.hasAcceptedDisclaimer]);

  // Task #109: log device class (platform/browser bucket + browser-vs-PWA)
  // once per authenticated session. Best-effort; failures never block the
  // user. The server parses platform/browser from the user-agent header so
  // we only POST the display mode the server can't see.
  //
  // We log once per *authenticated user id* in the lifetime of this tab.
  // Logging out resets the ref, and an account switch in the same tab is
  // treated as a fresh sign-in (different user id ⇒ new row).
  const lastLoggedSigninUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated) {
      lastLoggedSigninUserIdRef.current = null;
      return;
    }
    const currentUserId = user?.id ?? null;
    if (!currentUserId) return;
    if (lastLoggedSigninUserIdRef.current === currentUserId) return;
    lastLoggedSigninUserIdRef.current = currentUserId;
    const displayMode =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches
        ? "pwa"
        : "browser";
    apiRequest("POST", "/api/device-signin", { displayMode }).catch(() => {
      // Silent — analytics signal only.
    });
  }, [isAuthenticated, user?.id]);

  // Start the global real-time event stream once the user is authenticated,
  // and tear it down on logout / session loss so the next session opens a
  // fresh EventSource bound to the new auth cookie.
  useEffect(() => {
    if (isAuthenticated) {
      startRealtime();
      return () => stopRealtime();
    }
    stopRealtime();
  }, [isAuthenticated]);

  // Lock the encryption vault whenever the user signs out, OR when the
  // authenticated user-id changes (account switch in the same tab). This
  // prevents MEK/private-key bytes for one account from being reused by
  // another account's session.
  const lastUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const currentId = isAuthenticated ? (user?.id ?? null) : null;
    if (lastUserIdRef.current && currentId !== lastUserIdRef.current) {
      lockVault();
    }
    lastUserIdRef.current = currentId;
  }, [isAuthenticated, user?.id]);

  // Task #100: keep the vault idle-lock window in sync with the user's
  // preference, so a freshly unlocked session honors their choice without
  // needing a reload. Mutations in Settings also push the value optimistically.
  const { data: appPrefs } = useQuery<{ vaultIdleLockMinutes?: number }>({
    queryKey: ["/api/user/preferences"],
    enabled: !!isAuthenticated,
  });
  useEffect(() => {
    if (appPrefs?.vaultIdleLockMinutes) {
      setIdleLockMinutes(appPrefs.vaultIdleLockMinutes);
    }
  }, [appPrefs?.vaultIdleLockMinutes]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/resources" component={Resources} />
        <Route path="/terms" component={Terms} />
        <Route component={NotFound} />
      </Switch>
    );
  }

  // Show disclaimer for new users who haven't accepted yet
  const showDisclaimer = !!(isAuthenticated && user && !user.hasAcceptedDisclaimer && !disclaimerAccepted);

  // If authenticated but no role, or role was auto-assigned (hasSelectedRole = false),
  // show role selection so users can explicitly choose their role
  if (!user?.role || !user?.hasSelectedRole) {
    return (
      <>
        <BetaDisclaimerModal 
          open={showDisclaimer} 
          onAccepted={() => setDisclaimerAccepted(true)} 
        />
        <RoleSelection />
      </>
    );
  }

  // Require every account to have a display name. New users set one during
  // onboarding (RoleSelection); existing users who predate that requirement
  // are prompted once here before any authenticated route renders.
  if (!showDisclaimer && !user.firstName?.trim()) {
    return <DisplayNamePrompt />;
  }

  const style = {
    "--sidebar-width": "16rem",
  };

  return (
    <>
      <BetaDisclaimerModal 
        open={showDisclaimer} 
        onAccepted={() => setDisclaimerAccepted(true)} 
      />
      <SidebarProvider defaultOpen={false} style={style as React.CSSProperties}>
        <div className="flex h-screen w-full">
          <AppSidebar
            userRole={user.role as "sponsor" | "sponsee"}
            userName={user.firstName || user.email || "User"}
            userAvatar={user.profileImageUrl || undefined}
          />
          <div className="flex flex-col flex-1">
            <header
              className="flex items-center justify-between px-4 pb-4 border-b"
              style={{
                paddingTop: "max(1rem, env(safe-area-inset-top))",
              }}
            >
              <SidebarTrigger
                data-testid="button-sidebar-toggle"
                className="hidden md:flex"
              />
              <div className="flex items-center gap-1">
                <VaultStatusBadge />
                <NotificationCenter />
                <ThemeToggle />
              </div>
            </header>
            <main className="flex-1 overflow-auto pb-24 md:pb-0">
              <ErrorBoundary label="this page" resetKey={currentLocation}>
              <VaultGate>
              <SponsorHomeLaunchRedirect />
              <Switch>
                <Route path="/" component={Home} />
                <Route path="/profile" component={Profile} />
                <Route path="/family-tree" component={ExpandedFamilyTree} />
                <Route path="/journal" component={Journal} />
                <Route path="/shared-journal" component={SharedJournal} />
                <Route path="/journey" component={Journey} />
                <Route path="/triggers-cravings" component={TriggersCravings} />
                <Route path="/relapse-prevention">
                  {() => <Redirect to="/triggers-cravings" />}
                </Route>
                <Route path="/playbook" component={RecoveryPlaybook} />
                <Route path="/first-90-intro" component={First90Intro} />
                <Route path="/summary-archive" component={SummaryArchive} />
                <Route path="/saved-readings" component={SavedReadings} />
                <Route path="/anniversaries" component={Anniversaries} />
                <Route path="/service" component={ServicePage} />
                <Route path="/steps" component={StepsPage} />
                <Route path="/steps/:stepId" component={StepView} />
                <Route path="/sponsees" component={SponseesDashboard} />
                <Route path="/sponsees/:sponseeId/assignments" component={SponseeAssignments} />
                <Route path="/sponsees/:sponseeId/submissions" component={SponseeSubmissions} />
                <Route path="/sponsees/:sponseeId/messages" component={SponseeMessages} />
                <Route path="/messages" component={SponseeChat} />
                <Route path="/review/:sponseeId/:stepId" component={ReviewPage} />
                <Route path="/library" component={SponsorLibrary} />
                <Route path="/assignments/:id" component={AssignmentDetail} />
                <Route path="/assignments" component={AssignedTasks} />
                <Route path="/assign-tasks" component={AssignTasksToSponsee} />
                <Route path="/standard-tasks" component={StandardTasks} />
                <Route path="/community-portal" component={HomeGroupPortal} />
                <Route path="/gratitude-lists" component={GratitudeLists} />
                <Route path="/find-a-sponsor" component={FindASponsorPage} />
                <Route path="/inner-circle" component={InnerCircle} />
                <Route path="/sponsor-inquiries" component={SponsorContactInbox} />
                <Route path="/sponsor-portal" component={SponsorPortal} />
                <Route path="/sponsor-settings" component={SponsorSettings} />
                <Route path="/settings" component={Settings} />
                <Route path="/admin/device-stats" component={DeviceStats} />
                <Route path="/admin" component={AdminPortal} />
                <Route path="/whats-new/:id" component={WhatsNew} />
                <Route path="/contact" component={Contact} />
                <Route path="/faq" component={FAQ} />
                <Route path="/resources" component={Resources} />
                <Route path="/privacy-policy" component={PrivacyPolicy} />
                <Route path="/terms" component={Terms} />
                <Route component={NotFound} />
              </Switch>
              </VaultGate>
              </ErrorBoundary>
            </main>
            <BurningDesireButton />
            <BottomNav userRole={user.role as "sponsor" | "sponsee"} />
            <EnablePushPrompt />
            <IosPwaTextSizeHint />
          </div>
        </div>
      </SidebarProvider>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppearanceProvider>
        <TooltipProvider>
          <Toaster />
          {/* Task #348 — one Suspense boundary above the Router covers every
              lazy page (routes, RoleSelection, NotFound) with the same
              fallback the auth-loading state already uses. */}
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-screen">
                <div className="text-muted-foreground">Loading...</div>
              </div>
            }
          >
            <Router />
          </Suspense>
        </TooltipProvider>
      </AppearanceProvider>
    </QueryClientProvider>
  );
}

export default App;
